import { Asset, DenormalizedCustomizableQuestion, QuestionType } from '@packages/types'
import React from 'react'

import useThemeSettings from 'customizer/theme/useThemeSettings'
import ThemesFileUploader from 'themes/common/components/FileUploader'
import Translation from 'themes/common/components/Translation'

import LogoAnswer from '../answer/LogoAnswer'
import ScrollableAnswersRow from './ScrollableAnswersRow'

import './FileUploadQuestion.scss'

interface FileUploadQuestionProps {
  question: DenormalizedCustomizableQuestion
  answersOutOfStock: string[]
  browseText?: () => string
  selectAnswer: (answerId?: string) => void
  removeAnswer: (answerId: string) => void
  createLogoAnswer: (questionId: string, asset: Asset) => void
  createAssetAnswer: (questionId: string, value: Asset) => void
}

const FileUploadQuestion = ({
  selectAnswer,
  browseText,
  question,
  removeAnswer,
  createLogoAnswer,
  createAssetAnswer,
  answersOutOfStock,
}: FileUploadQuestionProps) => {
  const fileUploadStyle = useThemeSettings('fileUpload')

  const classNameModifiers = ['logo']
  if (question.answers.length === 0) classNameModifiers.push('empty')

  const handleCreateAnswer = (questionId: string, asset: Asset) => {
    if (question.type === QuestionType.Value) {
      return createAssetAnswer(questionId, asset)
    }

    return createLogoAnswer(questionId, asset)
  }

  return (
    <ScrollableAnswersRow
      classNameModifiers={classNameModifiers}
      selectedAnswerId={question.selectedAnswer?.id}
      key={question.id}
      disabled={question.answers.length === 0}
    >
      <ThemesFileUploader
        id={question.id}
        containerStyle={{
          backgroundColor: fileUploadStyle.backgroundColor,
          borderColor: fileUploadStyle.borderColor,
          borderRadius: fileUploadStyle.borderRadius,
        }}
        linkStyle={{
          color: fileUploadStyle.linkColor,
          fontSize: fileUploadStyle.fontSize,
        }}
        onChange={assets => handleCreateAnswer(question.id, assets[0])}
        extensions={
          question.allowedExtensionsByType ? Object.values(question.allowedExtensionsByType).flat() : undefined
        }
        dropText={() => null}
        browseText={browseText ? browseText : () => <Translation field="browseFilesMobile" />}
        loaderStyle={{ color: fileUploadStyle.fontColor }}
        description=""
      />
      {question.answers.map(answer => {
        const isOutOfStock = answersOutOfStock.includes(answer.id)

        return (
          <LogoAnswer
            key={answer.id}
            answer={answer}
            selected={answer.id === question.selectedAnswer?.id}
            onSelect={() => selectAnswer(answer.id)}
            onRemove={() => removeAnswer(answer.id)}
            isRemovable={answer.isPersonalisation}
            isOutOfStock={isOutOfStock}
          />
        )
      })}
    </ScrollableAnswersRow>
  )
}

export default FileUploadQuestion
